// Typed API methods for auth & onboarding

import { apiClient } from './api-client'
import type {
  MeResponse,
  OrgUser,
  TenantData,
  InviteUserPayload,
  UpdateUserPayload,
  UpdateProfilePayload,
  Order,
  OrderDetail,
  AllTenantOrder,
  OrderSupplierChanges,
  SupplierChangeGroup,
  SupplierChangeOrder,
  WidgetScope,
  WidgetTokenResponse,
} from '../types'

// --- Sign out ---

export function signOutApi(): Promise<void> {
  return apiClient.post('/api/auth/signout', {})
}

// --- Current User ---

export function getMe(): Promise<MeResponse> {
  return apiClient.get('/api/users/me')
}

export interface BootstrapMeError {
  status: number
  data: Record<string, unknown>
}

export async function bootstrapMe(getAccessToken: () => Promise<string>): Promise<MeResponse> {
  let token = ''
  try {
    token = (await getAccessToken()) || ''
  } catch {
    // AuthKit may not have processed the new session yet — fall through
    // to localStorage where the code-exchange token is already stored.
  }

  if (!token) {
    token = localStorage.getItem('access_token') || ''
  }

  if (!token) {
    const err: BootstrapMeError = { status: 401, data: {} }
    throw err
  }

  const headers = { Authorization: `Bearer ${token}` }
  return apiClient.get('/api/users/me', { headers })
}

export function updateProfile(payload: UpdateProfilePayload): Promise<MeResponse> {
  return apiClient.patch('/api/users/me', payload)
}

// --- Widget Tokens (WorkOS widgets) ---

export function fetchWidgetToken(scopes: WidgetScope[]): Promise<WidgetTokenResponse> {
  return apiClient.post<WidgetTokenResponse>('/api/users/widget-token', { scopes })
}

// --- Users (scoped to tenant) ---

interface PaginatedResponse<T> {
  count: number
  results: T[]
  next: string | null
  previous: string | null
}

export function getUsers(pageSize?: number): Promise<PaginatedResponse<OrgUser>> {
  const query = pageSize ? `?page_size=${pageSize}` : ''
  return apiClient.get(`/api/users/${query}`)
}

export function createUser(payload: InviteUserPayload): Promise<OrgUser> {
  return apiClient.post('/api/users/', payload)
}

export function updateUser(userId: string, payload: UpdateUserPayload): Promise<OrgUser> {
  return apiClient.patch(`/api/users/${userId}/`, payload)
}

export function deleteUser(userId: string): Promise<void> {
  return apiClient.delete(`/api/users/${userId}/`)
}

// --- Tenant Settings ---

export function getTenant(): Promise<TenantData> {
  return apiClient.get('/api/tenants/')
}

export function updateTenant(payload: Partial<TenantData>): Promise<TenantData> {
  return apiClient.patch('/api/tenants/', payload)
}

export function uploadTenantLogo(file: File): Promise<{ logo_url: string }> {
  const formData = new FormData()
  formData.append('logo', file)
  return apiClient.upload('/api/tenants/logo', formData)
}

export function deleteTenantLogo(): Promise<void> {
  return apiClient.delete('/api/tenants/logo')
}

// --- Orders ---

/**
 * Optional approval-queue filters for the orders list (APR-003).
 * All fields map 1:1 to backend query params on GET /api/orders/.
 */
export interface OrderFilters {
  status?: string
  assignedTo?: 'me'
  team?: 'me'
  approver?: string
  engineer?: string
  dateFrom?: string
  dateTo?: string
  minValue?: string
  maxValue?: string
  approvalReason?: string
  sort?: 'oldest' | 'newest'
}

export function getOrders(
  search?: string,
  page?: number,
  myOnly?: boolean,
  pageSize?: number,
  filters?: OrderFilters,
): Promise<PaginatedResponse<Order> & { total_amount?: string }> {
  const params = new URLSearchParams()
  if (search) params.append('search', search)
  if (page) params.append('page', page.toString())
  if (myOnly) params.append('my_orders', 'true')
  if (pageSize) params.append('page_size', pageSize.toString())

  if (filters) {
    if (filters.status) params.append('status', filters.status)
    if (filters.assignedTo) params.append('assigned_to', filters.assignedTo)
    if (filters.team) params.append('team', filters.team)
    if (filters.approver) params.append('approver', filters.approver)
    if (filters.engineer) params.append('engineer', filters.engineer)
    if (filters.dateFrom) params.append('date_from', filters.dateFrom)
    if (filters.dateTo) params.append('date_to', filters.dateTo)
    if (filters.minValue) params.append('min_value', filters.minValue)
    if (filters.maxValue) params.append('max_value', filters.maxValue)
    if (filters.approvalReason) params.append('approval_reason', filters.approvalReason)
    if (filters.sort) params.append('sort', filters.sort)
  }

  const queryString = params.toString()
  return apiClient.get(`/api/orders/${queryString ? '?' + queryString : ''}`)
}

export function getOrder(id: string): Promise<OrderDetail> {
  return apiClient.get(`/api/orders/${id}/`)
}

// Order approve/decline actions
export async function approveOrder(id: string, payload: { notes?: string }): Promise<OrderDetail> {
  return apiClient.post<OrderDetail>(`/api/orders/${id}/approve/`, payload)
}

// Approve WITHOUT submitting to the supplier. Offered only by the pre-prod UI
// so approvals can be exercised without placing a real supplier order; the
// order settles in `approved` and is never auto-submitted or retried.
export async function approveOrderWithoutSubmit(id: string, payload: { notes?: string }): Promise<OrderDetail> {
  return apiClient.post<OrderDetail>(`/api/orders/${id}/approve-without-submit/`, payload)
}

export async function declineOrder(id: string, payload: { reason: string }): Promise<OrderDetail> {
  return apiClient.post<OrderDetail>(`/api/orders/${id}/decline/`, payload)
}

// Withdraw an own order while it is awaiting approval (APR-007)
export async function withdrawOrder(id: string): Promise<OrderDetail> {
  return apiClient.post<OrderDetail>(`/api/orders/${id}/withdraw/`, {})
}

// Request cancellation of an already-sent order (CANCEL-002). The backend
// emails the branch (CC + Reply-To = engineer) and persists the request on
// the order. The order's status does NOT change — this is a request, not an
// instant cancellation.
export type CancellationReason =
  | 'no_longer_needed'
  | 'wrong_item'
  | 'got_elsewhere'
  | 'job_moved'
  | 'other'

export interface RequestCancellationBody {
  reason: CancellationReason
  note?: string
}

export async function requestOrderCancellation(
  id: string,
  body: RequestCancellationBody
): Promise<OrderDetail> {
  return apiClient.post<OrderDetail>(
    `/api/orders/${id}/cancellation-request/`,
    body
  )
}

// Mark an order as CANCELLED after the branch confirms off-platform
// (CANCEL-004). Only valid after a cancellation request has been sent.
export async function markOrderCancelled(id: string): Promise<OrderDetail> {
  return apiClient.post<OrderDetail>(`/api/orders/${id}/mark-cancelled/`, {})
}

// Add all items from a past order to the cart (APR-006 reorder)
export interface ReorderResult {
  success: boolean
  data: {
    added: number
    unavailable: string[]
    skipped: { product_name: string; reason: string }[]
    total_items: number
    branch_changed_to: string | null
  }
  message: string
}

export async function reorder(
  id: string,
  location?: { latitude: number; longitude: number },
): Promise<ReorderResult> {
  return apiClient.post<ReorderResult>(`/api/orders/${id}/reorder/`, location ?? {})
}

// Live supplier price + branch/DC stock per item, fetched on demand so the
// order detail renders instantly and these values hydrate in (APR-003a AC2).
export interface OrderLivePricingItem {
  id: string
  current_price: string | null
  branch_stock: number | null
  // Per-channel stock — the approval "was → now" compares the channel the
  // engineer saw (collection for collection orders, delivery for delivery).
  collection_stock: number | null
  delivery_stock: number | null
  dc_stock: number | null
  // Orderable ceiling (max(branch, dc)) — drives fulfillable qty (Codex P2).
  cap_total: number | null
  // Channel-specific ceiling — the order-detail display uses this so
  // availability/totals match the order's fulfilment channel (delivery vs
  // collection). Placement/capping keep using cap_total.
  channel_cap: number | null
  // Raw ISO dates + depot qty for the availability string (APR-003a AC2).
  branch_date: string | null
  later_date: string | null
  later_qty: number | null
  // Backorder tier: the line may be placed above (or with zero) stock, and
  // the channel date that applies then. Absent on older responses = off.
  backorder_allowed?: boolean
  backorder_date?: string | null
}

export interface OrderLivePricing {
  items: OrderLivePricingItem[]
}

export async function getOrderLivePricing(id: string): Promise<OrderLivePricing> {
  return apiClient.get<OrderLivePricing>(`/api/orders/${id}/live-pricing/`)
}

// Order activity log (APR-017) — a curated, chronological, sentence-form
// timeline of everything that happened to the order, oldest first.
export interface OrderActivityEvent {
  timestamp: string | null
  message: string
  // Optional sub-lines shown under the message (e.g. per-item breakdown on the
  // placement event).
  details?: string[]
}

export interface OrderActivity {
  results: OrderActivityEvent[]
}

export async function getOrderActivity(id: string): Promise<OrderActivity> {
  return apiClient.get<OrderActivity>(`/api/orders/${id}/activity/`)
}

// Live stock status for the approver queue (APR-009/010 list view).
export type OrderStockStatusValue =
  | 'out_of_stock'
  | 'partial'
  | 'stock_reduced'
  | 'date_changed'
  | 'no_change'
  | 'unknown'

export interface OrderStockStatus {
  stock_status: OrderStockStatusValue
  all_out_of_stock: boolean
  // Capped total the order would place at current stock (string) — matches the
  // detail page's total for a partial order. Absent for unknown/no-data results.
  available_total?: string | null
}

// Orders the supplier changed (PO-008), read-only, the oldest change first.
// The server limits them to the user's company, or every company for a SaaS
// admin. Without supplierId the reply also has each supplier's heading figures.
export interface SupplierChangesQuery {
  search?: string
  supplierId?: number | null
  page: number
  pageSize: number
}

export function getSupplierChanges(
  query: SupplierChangesQuery,
): Promise<PaginatedResponse<SupplierChangeOrder> & { suppliers?: SupplierChangeGroup[] }> {
  const params = new URLSearchParams()
  if (query.search) params.append('search', query.search)
  if (query.supplierId != null) params.append('supplier_id', query.supplierId.toString())
  params.append('page', query.page.toString())
  params.append('page_size', query.pageSize.toString())
  return apiClient.get(`/api/orders/supplier-changes/?${params.toString()}`)
}

// Every company's orders, newest first (PO-008 AC1). SaaS admins only.
export function getAllTenantOrders(query: { search?: string; page: number; pageSize: number }): Promise<PaginatedResponse<AllTenantOrder>> {
  const params = new URLSearchParams()
  if (query.search) params.append('search', query.search)
  params.append('page', query.page.toString())
  params.append('page_size', query.pageSize.toString())
  return apiClient.get(`/api/orders/all-tenants/?${params.toString()}`)
}

// One order's supplier changes (PO-009): lines, totals and timeline. 404 when
// the order has none or isn't the user's to see.
export function getOrderSupplierChanges(orderId: string): Promise<OrderSupplierChanges> {
  return apiClient.get(`/api/orders/${orderId}/supplier-changes/`)
}

// Batch live stock status for the given (visible) order IDs. Cached server-side
// per order (short TTL); only awaiting-approval orders the approver can see are
// returned. Pass force=true (manual Refresh) to bypass the cache for live data.
export async function getOrdersStockStatus(
  orderIds: string[],
  force = false,
): Promise<Record<string, OrderStockStatus>> {
  const res = await apiClient.post<{ statuses: Record<string, OrderStockStatus> }>(
    '/api/orders/stock-status/',
    { order_ids: orderIds, force },
  )
  return res.statuses ?? {}
}

// Order retry action (manual retry after failed submission)
export async function retryOrder(id: string): Promise<OrderDetail> {
  return apiClient.post<OrderDetail>(`/api/orders/${id}/retry/`, {})
}

// Order status refresh (fetch latest status from supplier API)
export async function refreshOrderStatus(id: string): Promise<OrderDetail> {
  return apiClient.post<OrderDetail>(`/api/orders/${id}/refresh_status/`, {})
}

// Manual status update for orders from suppliers without a live status API
// (Travis Perkins, CEF). The placing engineer can mark the order as completed
// or declined once the outcome is known. Backend enforces: caller is the
// order's placer, supplier has status_available=false, and current status is
// 'processing'.
export async function markOrderManualStatus(
  id: string,
  newStatus: 'completed' | 'fulfillment_declined',
): Promise<OrderDetail> {
  return apiClient.post<OrderDetail>(`/api/orders/${id}/manual-status/`, {
    status: newStatus,
  })
}
